import React, { useState, useReducer, useCallback, useEffect } from "react";
import isEqual from "react-fast-compare";
import {
  useSnackbar,
  OSSErrorMessage,
  useAnalyticsProvider,
  useLocalStorage
} from "@oss/components";
import sdfService from "../services/sdf.api";

const SDFActionsContext = React.createContext();

function targetingDataReducer(state, action) {
  switch (action.type) {
    case "STORE_TARGETING_DATA":
      return {
        ...state,
        ...action.data
      };
    case "SET_STATE_STATUS":
    case "STORE_SDF_PREVIEW":
    case "STORE_INVALID_CHANNELS":
    case "SET_APPLYING_PLACEMENTS":
    case "SET_SELECTED_AD_GROUP_IDS":
      const { objectKey, data } = action;
      return {
        ...state,
        [objectKey]: data
      };
    case "SET_UPLOADED_FILES":
      return {
        ...state,
        uploaded_files: {
          ...action.data
        }
      };
    case "RESTART_FLOW":
      return {
        ...initialTargetingState
      };
    case "CLEAR_CURRENT_PLACEMENTS":
      return {
        ...state,
        bundle_info: {
          ad_groups: []
        },
        id: "",
        selectedAdGroupIds: [],
        invalid_channels: null,
        preview: [],
        state: "unbaked"
      };
    default:
      return state;
  }
}

const initialTargetingState = {
  applying_placements: false,
  bundle_info: {
    ad_groups: []
  },
  id: "",
  invalid_channels: null,
  preview: [],
  selectedAdGroupIds: [],
  selected_ad_groups: null,
  state: "unbaked",
  targeting_tactic: "inclusion",
  uploaded_files: {
    sdf: [],
    placements: []
  }
};

const SDFActionsProvider = props => {
  const [userDownloadedFile, setUserDownloadedFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [localStoreageState, setLocalstoreageState] =
    useLocalStorage("targetingState");

  //initialize the reducer with the local storage version
  // if null, use the default state
  const [state, dispatch] = useReducer(
    targetingDataReducer,
    localStoreageState ?? initialTargetingState
  );
  const setTrackerData = useAnalyticsProvider();

  //if the reducer updates the state, this effect will update the local storage
  useEffect(() => {
    !isEqual(localStoreageState, state) && setLocalstoreageState(state);
  }, [state, setLocalstoreageState, localStoreageState]);

  const errorSnackbar = error => {
    let msg = error?.response?.data || error.message;
    if (error?.response?.status === 500) {
      msg = "An error occurred when processing your request.";
    }
    setTrackerData({
      method: "event",
      category: "SAT API call",
      action: "api_error",
      label: msg
    });

    enqueueSnackbar({
      title: "Error",
      message: <OSSErrorMessage error={msg} />,
      options: {
        persist: true,
        preventDuplicate: true,
        variant: "error"
      }
    });
  };

  const handleFormSubmit = async formData => {
    const sdfFile = formData.get("sdf");
    const slateFile = formData.get("channel_slate");
    let msg = "";
    if (sdfFile) {
      msg = `sdf file name:${sdfFile.name}, size:${sdfFile.size},type:${sdfFile.type}`;
    }
    if (slateFile) {
      msg += `; Slate file name:${slateFile.name}, size:${slateFile.size},type:${slateFile.type}`;
    }

    setIsLoading(true);

    // If text file, allow ad group selection
    const response = await sdfService.post("/targeted-sdfs", formData);
    setIsLoading(false);
    setTrackerData({
      method: "event",
      category: "SAT Upload",
      action: "upload_success",
      label: msg
    });
    return response;
  };

  const handleTargetedPatch = async (id, filtered) => {
    try {
      const response = await sdfService.patch(
        `/targeted-sdfs/${id}?format=json`,
        {
          selected_ad_groups: filtered
        }
      );
      return response;
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  const downloadZipFile = async (id, max_channels_per_file) => {
    // https://prescreen-api.dev.doubleverify.app/ads/v1/targeted-sdfs/4c49a28e-84b4-4d2d-8fae-53927439ce8e/download/?zip=true&max_channels_per_file=5000
    const response = await sdfService(
      `/targeted-sdfs/${id}/download?zip=true&max_channels_per_file=${max_channels_per_file}`,
      {
        // We need to tell axios that it needs to accept binary (which is here as arrayBuffer)
        responseType: "arraybuffer",
        headers: {
          "Content-Type": "application/zip"
        }
      }
    );
    setUserDownloadedFile(true);
    setIsLoading(false);
    return response;
  };

  /**
   *
   * @param {string} id targeted-sdf id that contains all ad_groups
   * @param {string} format csv or json will return string to be output as csv or json to render table
   * @param {boolean} preview if true, will return additional columns of information from sdf
   */
  const fetchFormattedInformation = async (
    id,
    format = "csv",
    preview = false
  ) => {
    try {
      const response = await sdfService(
        `/targeted-sdfs/${id}/download?format=${format}${
          preview ? `&preview=true` : ""
        }`
      );

      if (format === "json") {
        // We want to store the preview data for the confirmation table
        dispatch({
          type: "STORE_SDF_PREVIEW",
          objectKey: "preview",
          data: response.data
        });
      }
      return response;
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  const checkChannelValidationState = async id => {
    const res = await sdfService(`/targeted-sdfs/${id}`);
    const { state: validationState, invalid_channels } = res.data;
    if (validationState === "done") {
      dispatch({
        type: "STORE_INVALID_CHANNELS",
        objectKey: "invalid_channels",
        data: invalid_channels
      });
    }
    return res.data;
  };

  const setSelectedAdGroupIds = selections => {
    dispatch({
      type: "SET_SELECTED_AD_GROUP_IDS",
      objectKey: "selectedAdGroupIds",
      data: selections
    });
  };

  const restartFlow = useCallback(() => {
    setIsLoading(false);
    setUserDownloadedFile(false);
    return dispatch({
      type: "RESTART_FLOW"
    });
  }, []);

  // want to check if the validation process has return at least one valid channel
  // it is possible, in theory, that the validation process has not returned any valid channels
  // only need to check the first ad group, as all ad groups will have the same valid channels
  function hasValidChannelInclusionPlacements(state) {
    const { applying_placements, targeting_tactic } = state;

    // if we are not applying placements, we can skip this check
    if (!applying_placements) return true;

    return targeting_tactic === "inclusion"
      ? Boolean(
          state.preview[0].placement_targeting_youtube_channels_include
            ?.length > 0
        )
      : Boolean(
          state.preview[0].placement_targeting_youtube_channels_exclude
            ?.length > 0
        );
  }

  return (
    <SDFActionsContext.Provider
      {...props}
      value={{
        dispatch,
        state,
        isLoading,
        errorSnackbar,
        restartFlow,
        setIsLoading,
        downloadZipFile,
        handleFormSubmit,
        userDownloadedFile,
        handleTargetedPatch,
        setUserDownloadedFile,
        setSelectedAdGroupIds,
        fetchFormattedInformation,
        checkChannelValidationState,
        hasValidChannelInclusionPlacements
      }}
    />
  );
};

function useSDFActions() {
  const context = React.useContext(SDFActionsContext);
  if (context === undefined) {
    throw new Error("useSDFActions must be used within a SDFActionsProvider");
  }
  return context;
}

export { SDFActionsContext, SDFActionsProvider, useSDFActions };
